import type { GrantMetadata, Investigator } from '@generated/form-types/grant';
import {
  appendGrantElement,
  appendGrantElementIf,
  appendRelationsElement,
} from '../xml-helpers';

type GrantRelationship = NonNullable<GrantMetadata['relationships']>[number];

/**
 * Builds `<grant>` from Grant metadata.
 *
 * XSD order (`grant_id0.2.0.xsd`): project, award-number, award-start-date,
 * rel:program, doi_data.
 */
export function buildGrant(
  parent: Element,
  meta: GrantMetadata,
  removeRelationships = false
): Element {
  const grant = appendGrantElement(parent, 'grant');

  // 1. project
  buildProject(grant, meta);

  // 2. award-number
  appendGrantElement(grant, 'award-number', meta['award-number']);

  // 3. award-start-date
  appendGrantElementIf(grant, 'award-start-date', meta['award-start-date']);

  // 4. rel:program (relationships)
  buildRelationships(grant, meta.relationships, removeRelationships);

  // 5. doi_data
  const doiData = appendGrantElement(grant, 'doi_data');
  appendGrantElement(doiData, 'doi', meta.doi);
  appendGrantElement(doiData, 'resource', meta.landingPageUrl);

  return grant;
}

/**
 * XSD order (`project`): project-title, investigators, description,
 * award_amount, funding, award-dates (unused).
 */
function buildProject(grant: Element, meta: GrantMetadata): void {
  const project = appendGrantElement(grant, 'project');

  // 1. project-title
  for (const title of meta['project-title']) {
    const attrs = title.lang ? { 'xml:lang': title.lang } : undefined;
    appendGrantElement(project, 'project-title', title['title-text'], attrs);
  }

  // 2. investigators
  buildInvestigators(project, meta.investigators);

  // 3. description
  for (const description of meta.description ?? []) {
    if (!description['description-text']) continue;
    const attrs = description.lang ? { 'xml:lang': description.lang } : undefined;
    appendGrantElement(
      project,
      'description',
      description['description-text'],
      attrs
    );
  }

  // 4. award_amount
  if (meta.award_amount?.amount != null) {
    const attrs = meta.award_amount.currency
      ? { currency: meta.award_amount.currency }
      : undefined;
    appendGrantElement(
      project,
      'award_amount',
      String(meta.award_amount.amount),
      attrs
    );
  }

  // 5. funding
  buildFunding(project, meta.funding);
}

/**
 * Grant ID 0.2.0 `funding` is an xs:choice: ROR (+ optional scheme) or
 * funder-name + funder-id (+ optional scheme). JSON may keep both name and
 * ROR for the form; XML follows the XSD branch.
 */
function buildFunding(
  project: Element,
  funding: GrantMetadata['funding']
): void {
  const fundingEl = appendGrantElement(project, 'funding', null, {
    'funding-percentage': '100',
    'funding-type': funding['funding-type'],
  });

  if (funding.funderRorId) {
    appendGrantElement(fundingEl, 'ROR', funding.funderRorId);
    appendGrantElementIf(
      fundingEl,
      'funding-scheme',
      funding['funding-scheme']
    );
    return;
  }

  appendGrantElement(fundingEl, 'funder-name', funding['funder-name']);
  appendGrantElement(fundingEl, 'funder-id', funding['funder-id']);
  appendGrantElementIf(fundingEl, 'funding-scheme', funding['funding-scheme']);
}

/**
 * One `<program>` in the relations namespace, with a `<related_item>` per
 * complete relationship row. An empty program is written only when the
 * imported grant had relationships and none remain.
 */
function buildRelationships(
  grant: Element,
  relationships: GrantMetadata['relationships'],
  removeRelationships = false
): void {
  const complete = (relationships ?? []).filter(
    (relationship): relationship is GrantRelationship & {
      type_of_relationship: string;
      related_doi: string;
    } => Boolean(relationship.type_of_relationship && relationship.related_doi)
  );
  if (!complete.length) {
    if (removeRelationships) {
      appendRelationsElement(grant, 'program');
    }
    return;
  }

  const program = appendRelationsElement(grant, 'program');
  for (const relationship of complete) {
    const relatedItem = appendRelationsElement(program, 'related_item');
    appendRelationsElement(
      relatedItem,
      'inter_work_relation',
      relationship.related_doi,
      {
        'identifier-type': 'doi',
        'relationship-type': relationship.type_of_relationship,
      }
    );
  }
}

function buildInvestigators(
  project: Element,
  investigators: Investigator[] | undefined
): void {
  if (!investigators?.length) return;

  const wrapper = appendGrantElement(project, 'investigators');
  for (const investigator of investigators) {
    const person = appendGrantElement(wrapper, 'person', null, {
      role: investigator.role,
    });

    // XSD order (`person`): givenName, familyName, alternateName,
    // affiliation, ORCID
    appendGrantElementIf(person, 'givenName', investigator.givenName);
    appendGrantElementIf(person, 'familyName', investigator.familyName);

    const institution = investigator.affiliation?.institution;
    if (institution) {
      const affiliation = appendGrantElement(person, 'affiliation');
      appendGrantElement(affiliation, 'institution', institution);
      appendGrantElementIf(affiliation, 'ROR', investigator.affiliation?.ror);
    }

    appendGrantElementIf(person, 'ORCID', investigator.orcid);
  }
}
